import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence, } from 'framer-motion';
import { ChevronDown, ArrowRight, ArrowUpRight, Mail, MapPin, } from 'lucide-react';
import DotNavigation from '@/components/layout/DotNavigation';
import BackToTopButton from '@/components/ui/BackToTopButton';
import Image from '@/components/ui/Image';
import AnimatedSection from '@/components/ui/AnimatedSection';
import SectionTitle from '@/components/ui/SectionTitle';
import JumpButton from '@/components/ui/JumpButton';
import { services } from '@/data/services';
import { dailySchedule, recruitmentInfo, } from '@/data/recruit';
import { useProjects } from '@/hooks/useProjects';
const companyImages = [
    { src: '/images/home/company-1.webp', alt: '作業中の様子' },
    { src: '/images/home/company-2.webp', alt: '社内ミーティング' },
    { src: '/images/home/company-3.webp', alt: '設備機器' },
    { src: '/images/home/company-4.webp', alt: '完成した電気設備' },
    { src: '/images/home/company-5.webp', alt: '作業ツール' },
    { src: '/images/home/company-6.webp', alt: '社員の集合写真' },
];
export default function Home() {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hoveredService, setHoveredService] = useState(null);
    const [hoveredProject, setHoveredProject] = useState(null);
    const { scrollY } = useScroll();
    const opacity = useTransform(scrollY, [0, 300], [1, 0]);
    const y = useTransform(scrollY, [0, 300], [0, 100]);
    // Use Supabase data for projects
    const { projects } = useProjects();
    useEffect(() => {
        setIsLoaded(true);
    }, []);
    return (<main className='relative'>
      <DotNavigation sections={[
            'hero',
            'services',
            'company',
            'recruit',
            'works',
            'contact',
        ]}/>

      <section id='hero' className='relative w-full h-screen overflow-hidden'>
        <motion.div className='absolute inset-0' style={{ y, opacity }}>
          <img src='/images/hero/bg.webp' alt='盛電設備工業の施工現場' className='w-full h-full object-cover'/>
          <div className='absolute inset-0 bg-gradient-to-b from-navy/40 via-navy/30 to-navy/60'/>
        </motion.div>

        <div className='relative z-10 h-full flex flex-col justify-center items-start px-6 md:px-12 lg:px-24'>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={isLoaded ? { opacity: 1, y: 0 } : {}} transition={{
            duration: 0.8,
            delay: 0.3,
            ease: [0.22, 1, 0.36, 1],
        }} className='max-w-4xl'>
            <h1 className='text-6xl md:text-7xl lg:text-8xl text-white font-bebas tracking-wider leading-tight'>
              CREATE GOOD POWER
            </h1>

            <p className='mt-6 text-xl md:text-2xl text-white font-noto font-light'>
              電気と設備で、街に「いい空気」を。
            </p>

            <p className='mt-4 text-lg text-white/80 max-w-2xl'>
              盛電設備工業株式会社は、各種配管工事・電気設備工事・太陽光発電工事をワンストップで提供する
              岩手県盛岡市の総合設備企業です。
            </p>

            <div className='mt-10 flex flex-wrap gap-4'>
              <Link to='/services' className='btn-secondary'>
                サービスを見る
              </Link>
              <Link to='/contact' className='btn-outline border-white text-white hover:bg-white hover:text-navy'>
                お問い合わせ
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div className='absolute bottom-10 left-1/2 -translate-x-1/2 z-10' initial={{ opacity: 0, y: -20 }} animate={isLoaded ? { opacity: 1, y: 0 } : {}} transition={{
            duration: 0.8,
            delay: 1,
            ease: 'easeOut',
        }}>
          <a href='#services' className='w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center transition-all hover:-translate-y-1 hover:bg-white/20' aria-label='サービスセクションへスクロール'>
            <ChevronDown className='text-white' size={24}/>
          </a>
        </motion.div>
      </section>

      <AnimatedSection id='services' className='bg-white'>
        <div className='section-container'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
            <div className='relative h-full order-2 lg:order-1'>
              <Image src='/images/home/services-main.webp' alt='電気設備工事の様子' className='w-full h-full object-cover rounded-lg' priority/>
            </div>

            <div className='order-1 lg:order-2'>
              <SectionTitle title='SERVICE' outlined/>

              <div className='space-y-8 mt-10'>
                {services.map((service) => (<div key={service.id} className='bg-lightgray rounded-lg p-6 relative overflow-hidden transition-all duration-300 hover:shadow-lg' onMouseEnter={() => setHoveredService(service.id)} onMouseLeave={() => setHoveredService(null)}>
                    <div className='relative z-10'>
                      <h3 className='text-3xl font-bebas tracking-wide text-navy'>
                        {service.title}
                      </h3>
                      <p className='text-lg font-noto font-medium mt-1 text-navy'>
                        {service.titleJa}
                      </p>
                      <p className='mt-4 text-navy/80 font-noto'>
                        {service.description}
                      </p>
                      <div className='mt-4'>
                        <Link to='/services' className='group flex items-center text-navy font-medium transition-colors hover:text-skyblue'>
                          <span>詳しく見る</span>
                          <ArrowRight size={16} className='ml-1 transition-transform group-hover:translate-x-1'/>
                        </Link>
                      </div>
                    </div>

                    <AnimatePresence>
                      {hoveredService === service.id && (<motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 0.07, x: 0 }} exit={{ opacity: 0, x: 50 }} transition={{ duration: 0.4 }} className='absolute top-0 right-0 h-full w-1/2'>
                          <Image src={service.image} alt={service.title} className='object-cover'/>
                        </motion.div>)}
                    </AnimatePresence>
                  </div>))}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id='company' className='bg-white lg:bg-gray-50' fullHeight={false}>
        <div className='h-full grid grid-cols-1 lg:grid-cols-12'>
          <div className='lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-1 lg:gap-3'>
            {companyImages.map((image, index) => (<motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: 'easeOut',
            }} viewport={{ once: true }} className='relative aspect-square'>
                <img src={image.src} alt={image.alt} className='w-full h-full object-cover' loading='lazy'/>
              </motion.div>))}
          </div>

          <div className='lg:col-span-5 bg-navy p-8 md:p-12 lg:p-16 flex flex-col justify-center'>
            <h2 className='text-5xl md:text-6xl font-bebas tracking-wider text-white'>
              ABOUT US
            </h2>

            <div className='mt-6 space-y-4 text-white/90 font-noto'>
              <p>
                盛電設備工業は、2018年に盛岡で設立された技術者集団です。各種配管工事・電気設備工事・太陽光発電工事をワンストップで提供する強みを生かし、岩手県を中心に様々なプロジェクトに携わってきました。
              </p>
              <p>
                最新の技術と柔軟な発想で、施工からメンテナンスまで一貫したサービスを提供しています。「電気と設備で、街に『いい空気』を」をモットーに、環境と人に優しい設備づくりを目指します。
              </p>
            </div>

            <div className='mt-10 flex justify-end'>
              <JumpButton href='/about'/>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id='recruit' className='bg-white'>
        <div className='section-container'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
            <div>
              <SectionTitle title='RECRUIT'/>

              <p className='text-lg text-navy/80 max-w-lg'>
                盛電設備工業では、施工管理、配管工、電気工事士、営業職などの人材を募集しています。
                未経験者も歓迎。若いチームで技術を磨き、キャリアを築きませんか？
              </p>

              <div className='mt-8'>
                <Link to='/recruit' className='btn-primary'>
                  採用情報を詳しく見る
                </Link>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-6'>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} viewport={{ once: true }} className='bg-lightgray p-6 rounded-lg'>
                <h3 className='text-2xl font-bebas text-navy'>OUR CULTURE</h3>
                <ul className='mt-4 space-y-2 text-navy/80'>
                  <li>• 若いチームが活躍する活気ある環境</li>
                  <li>• 資格取得を積極的にサポート</li>
                  <li>• 個人の成長とワークライフバランスを重視</li>
                  <li>• アイデアを積極的に取り入れる風土</li>
                </ul>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} viewport={{ once: true }} className='bg-lightgray p-6 rounded-lg'>
                <h3 className='text-2xl font-bebas text-navy'>ONE DAY</h3>
                <div className='mt-4 space-y-2 text-sm text-navy/80'>
                  {dailySchedule.map((item, index) => (<div key={index} className='flex'>
                      <span className='w-14 font-medium'>{item.time}</span>
                      <span>{item.activity}</span>
                    </div>))}
                  <div className='text-navy text-xs italic mt-1'>
                    ※詳細は採用ページをご覧ください
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} viewport={{ once: true }} className='bg-lightgray p-6 rounded-lg'>
                <h3 className='text-2xl font-bebas text-navy'>REQUIREMENTS</h3>
                <ul className='mt-4 space-y-2 text-navy/80'>
                  {recruitmentInfo.positions.map((position, index) => (<li key={index}>• {position.title}</li>))}
                </ul>
                <p className='text-navy text-xs italic mt-3'>
                  経験・スキル・資格など詳細は採用ページをご覧ください
                </p>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} viewport={{ once: true }} className='bg-lightgray p-6 rounded-lg'>
                <h3 className='text-2xl font-bebas text-navy'>COMPANY STATS</h3>
                <div className='mt-4 space-y-4'>
                  <div>
                    <p className='text-5xl font-bebas text-navy leading-none'>9</p>
                    <p className='mt-1 text-sm text-navy/70'>社員数（名）</p>
                  </div>
                  <div>
                    <p className='text-5xl font-bebas text-navy leading-none'>37</p>
                    <p className='mt-1 text-sm text-navy/70'>平均年齢（歳）</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id='works' className='bg-gray-50' fullHeight={false}>
        <div className='section-container'>
          <SectionTitle title='WORKS' outlined/>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {projects.filter(p => p.status === 'published').slice(0, 6).map((project, index) => (<motion.div key={project.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: 'easeOut',
            }} viewport={{ once: true }} className='group relative' onMouseEnter={() => setHoveredProject(project.id)} onMouseLeave={() => setHoveredProject(null)}>
                <Link to={`/works/${project.slug}`}>
                  <div className='relative aspect-[4/3] overflow-hidden rounded-lg'>
                    <img src={project.image_url} alt={project.title} className='w-full h-full object-cover transition-transform duration-500 group-hover:scale-105'/>

                    <AnimatePresence>
                      {hoveredProject === project.id && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className='absolute inset-0 bg-navy/80 flex flex-col justify-end p-6'>
                          <div>
                            <p className='text-white/70 text-sm font-medium'>
                              {project.category}
                            </p>
                            <h3 className='text-white text-xl font-noto font-medium mt-1'>
                              {project.title}
                            </h3>
                            <p className='text-white/70 text-sm mt-1'>
                              {project.location}
                            </p>
                          </div>

                          <div className='mt-4 flex justify-end'>
                            <div className='w-10 h-10 rounded-full bg-white flex items-center justify-center'>
                              <ArrowUpRight className='text-navy' size={18}/>
                            </div>
                          </div>
                        </motion.div>)}
                    </AnimatePresence>
                  </div>
                </Link>
              </motion.div>))}
          </div>

          <div className='mt-12 text-center'>
            <Link to='/works' className='btn-outline'>
              すべての施工事例を見る
            </Link>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id='contact' className='relative'>
        <div className='absolute inset-0'>
          <img src='/images/home/contact.webp' alt='お問い合わせ背景' className='w-full h-full object-cover'/>
          <div className='absolute inset-0 bg-navy/40'/>
        </div>

        <div className='relative z-10 section-container flex flex-col justify-center min-h-screen'>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <h2 className='text-7xl md:text-8xl font-bebas tracking-wider text-white'>
              GET IN TOUCH.
            </h2>

            <p className='text-xl font-noto text-white/80 mt-6 max-w-2xl'>
              お問い合わせやご相談は、以下のフォームよりお気軽にご連絡ください。
              電気設備、空調設備、太陽光発電に関することなど、専門スタッフが丁寧にお答えします。
            </p>

            <div className='mt-12'>
              <Link to='/contact' className='btn-secondary'>
                お問い合わせフォーム
              </Link>
            </div>
          </motion.div>

          <div className='absolute bottom-0 right-0 opacity-20 pointer-events-none'>
            <h2 className='text-[200px] font-bebas text-white leading-none'>
              MORIDEN
            </h2>
          </div>
        </div>
      </AnimatedSection>

      <footer className='bg-navy text-white'>
        <div className='section-container py-16 md:py-24'>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12'>
            <div>
              <Link to='/' className='text-white font-bebas text-3xl tracking-wider transition-opacity hover:opacity-80'>
                MORIDEN SETSUBI
              </Link>

              <ul className='mt-6 space-y-3 text-white/80'>
                <li className='flex items-start'>
                  <MapPin size={18} className='mr-2 mt-1 shrink-0'/>
                  <span>〒020-0839 岩手県盛岡市津志田南3-13-15</span>
                </li>
                <li className='flex items-center'>
                  <Mail size={18} className='mr-2 shrink-0'/>
                  <a href='mailto:info@moriden-setubi.com' className='hover:text-white transition-colors'>
                    info@moriden-setubi.com
                  </a>
                </li>
              </ul>

            </div>

            <div className='lg:col-span-2'>
              <h2 className='text-xl font-medium mb-6'>サイトマップ</h2>
              <nav className='grid grid-cols-2 gap-x-8 gap-y-4'>
                <Link to='/about' className='text-white/80 hover:text-white transition-colors'>
                  ABOUT US
                </Link>
                <Link to='/services' className='text-white/80 hover:text-white transition-colors'>
                  SERVICE
                </Link>
                <Link to='/works' className='text-white/80 hover:text-white transition-colors'>
                  WORKS
                </Link>
                <Link to='/recruit' className='text-white/80 hover:text-white transition-colors'>
                  RECRUIT
                </Link>
                <Link to='/contact' className='text-white/80 hover:text-white transition-colors'>
                  CONTACT
                </Link>
              </nav>
            </div>

            <div>
              <h2 className='text-xl font-medium mb-6'>お問い合わせ</h2>
              <p className='text-white/80 mb-6'>
                お見積り・ご相談は無料です。お気軽にお問い合わせください。
              </p>
              <Link to='/contact' className='text-white border border-white/30 px-6 py-3 rounded-full hover:bg-white/10 transition-colors text-sm inline-block'>
                お問い合わせフォームへ
              </Link>
            </div>
          </div>
        </div>

        <div className='border-t border-white/10'>
          <div className='container mx-auto px-6 py-6'>
            <div className='flex flex-col md:flex-row justify-between items-center text-sm text-white/60'>
              <p>
                © 2026 MORIDEN SETSUBI INDUSTRY CO., LTD. All Rights Reserved.
              </p>
              <div className='mt-4 md:mt-0 space-x-6'>
                <Link to='/privacy' className='hover:text-white transition-colors'>
                  プライバシーポリシー
                </Link>
                <Link to='/terms' className='hover:text-white transition-colors'>
                  利用規約
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <BackToTopButton />
    </main>);
}
